import createSlackService from 'app/Integrations/Slack'

interface UserData {
  uid: string
  id: string
  full_name: string
  role: string
  avatarUrl: string
  tenantId: string
  policiesAccepted?: boolean
  start_date: string
  group: string
  end_date: string
  email: string
  onboardingEnded?: string
  onboarding?: string
  image_url: string
}

interface TaskData {
  id: string
  task_name: string
  description?: string
  priority: string
  owner: string
  duedate: string
  status: string
  tenantId: string
  created_at?: string
}

interface Integration {
  id: string
  name: string
  status: string
  access_token?: string
  team_id?: string
  selectedChannel?: string
  [key: string]: any
}

export class IntegrationNotifications {
  private integrations: Integration[]
  private currentUser: any

  constructor(integrations: Integration[], currentUser: any) {
    this.integrations = integrations
    this.currentUser = currentUser
  }

  /**
   * Send onboarding reminder to all connected integrations
   */
  async sendOnboardingReminder(employee: UserData): Promise<{ success: boolean; errors: string[]; successfulIntegrations: string[] }> {
    const integrationMethods = [
      { method: this.sendSlackReminder(employee), name: 'Slack' },
      // Add more integrations here in the future
      // { method: this.sendTeamsReminder(employee), name: 'Teams' },
      // { method: this.sendDiscordReminder(employee), name: 'Discord' },
      // { method: this.sendEmailReminder(employee), name: 'Email' },
    ]

    const results = await Promise.allSettled(integrationMethods.map(item => item.method))

    const errors: string[] = []
    const successfulIntegrations: string[] = []
    let hasSuccess = false

    results.forEach((result, index) => {
      const integrationName = integrationMethods[index].name
      if (result.status === 'rejected') {
        errors.push(`${integrationName} failed: ${result.reason}`)
      } else if (result.value.success) {
        hasSuccess = true
        successfulIntegrations.push(integrationName)
      } else {
        errors.push(result.value.error || `${integrationName} failed`)
      }
    })

    return {
      success: hasSuccess,
      errors,
      successfulIntegrations,
    }
  }

  /**
   * Send task reminder to all connected integrations
   */
  async sendTaskReminder(task: TaskData, employee: UserData): Promise<{ success: boolean; errors: string[]; successfulIntegrations: string[] }> {
    const integrationMethods = [
      { method: this.sendSlackTaskReminder(task, employee), name: 'Slack' },
      // Add more integrations here in the future
      // { method: this.sendTeamsTaskReminder(task, employee), name: 'Teams' },
      // { method: this.sendDiscordTaskReminder(task, employee), name: 'Discord' },
      // { method: this.sendEmailTaskReminder(task, employee), name: 'Email' },
    ]

    const results = await Promise.allSettled(integrationMethods.map(item => item.method))

    const errors: string[] = []
    const successfulIntegrations: string[] = []
    let hasSuccess = false

    results.forEach((result, index) => {
      const integrationName = integrationMethods[index].name
      if (result.status === 'rejected') {
        errors.push(`${integrationName} failed: ${result.reason}`)
      } else if (result.value.success) {
        hasSuccess = true
        successfulIntegrations.push(integrationName)
      } else {
        errors.push(result.value.error || `${integrationName} failed`)
      }
    })

    return {
      success: hasSuccess,
      errors,
      successfulIntegrations,
    }
  }

  /**
   * Send reminder via Slack
   */
  private async sendSlackReminder(employee: UserData): Promise<{ success: boolean; error?: string }> {
    try {
      const slackIntegration = this.integrations.find(
        (int) => int.name === 'Slack' && int.status === 'Connected'
      )

      if (!slackIntegration) {
        return { success: false, error: 'Slack integration is not connected' }
      }

      if (!slackIntegration.access_token || !slackIntegration.team_id) {
        return { success: false, error: 'Slack integration is missing access token or team ID' }
      }

      const channelId = slackIntegration.selectedChannel
      if (!channelId) {
        return { success: false, error: 'No Slack channel selected for notifications' }
      }

      if (!this.currentUser?.tenantId) {
        return { success: false, error: 'Tenant ID is missing' }
      }

      const slackService = createSlackService(
        slackIntegration.access_token,
        slackIntegration.team_id
      )

      const joinResult = await slackService.joinChannel(channelId)
      if (!joinResult.success && joinResult.error !== 'already_in_channel') {
        return { success: false, error: `Failed to join Slack channel: ${joinResult.error}` }
      }

      const blocks = [
        {
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: `*🔔 Onboarding Reminder for ${employee.full_name}*`,
          },
        },
        {
          type: 'section',
          fields: [
            {
              type: 'mrkdwn',
              text: `*Email:*\n${employee.email}`,
            },
            {
              type: 'mrkdwn',
              text: `*Role:*\n${employee.role}`,
            },
            {
              type: 'mrkdwn',
              text: `*Group:*\n${employee.group}`,
            },
            {
              type: 'mrkdwn',
              text: `*Start Date:*\n${employee.start_date}`,
            },
            {
              type: 'mrkdwn',
              text: `*Onboarding Status:*\n${
                employee.onboarding === 'Completed' ? '✅ Completed' : '❗ Incomplete'
              }`,
            },
          ],
        },
        {
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: `Hi *${employee.full_name}*, it looks like you haven't completed your onboarding yet. Please log in and finish the pending steps to stay on track.`,
          },
        },
      ]

      const FIREBASE_FUNCTION_URL = 'https://us-central1-slate-134a0.cloudfunctions.net/slackProxy'
      const response = await fetch(FIREBASE_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'postMessage',
          accessToken: slackIntegration.access_token,
          channelId,
          message: blocks,
        }),
      })

      const result = await response.json()

      if (result.success) {
        return { success: true }
      } else {
        return { success: false, error: result.error || 'Failed to send Slack message' }
      }
    } catch (error: any) {
      console.error('Error sending Slack reminder:', error)
      return { success: false, error: error.message || 'Unknown error occurred' }
    }
  }

  /**
   * Send task reminder via Slack
   */
  private async sendSlackTaskReminder(task: TaskData, employee: UserData): Promise<{ success: boolean; error?: string }> {
    try {
      const slackIntegration = this.integrations.find(
        (int) => int.name === 'Slack' && int.status === 'Connected'
      )

      if (!slackIntegration) {
        return { success: false, error: 'Slack integration is not connected' }
      }

      if (!slackIntegration.access_token || !slackIntegration.team_id) {
        return { success: false, error: 'Slack integration is missing access token or team ID' }
      }

      const channelId = slackIntegration.selectedChannel
      if (!channelId) {
        return { success: false, error: 'No Slack channel selected for notifications' }
      }

      if (!this.currentUser?.tenantId) {
        return { success: false, error: 'Tenant ID is missing' }
      }

      const slackService = createSlackService(
        slackIntegration.access_token,
        slackIntegration.team_id
      )

      const joinResult = await slackService.joinChannel(channelId)
      if (!joinResult.success && joinResult.error !== 'already_in_channel') {
        return { success: false, error: `Failed to join Slack channel: ${joinResult.error}` }
      }

      const blocks = [
        {
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: `*📋 Task Reminder for ${employee.full_name}*`,
          },
        },
        {
          type: 'section',
          fields: [
            {
              type: 'mrkdwn',
              text: `*Task:*\n${task.task_name}`,
            },
            {
              type: 'mrkdwn',
              text: `*Priority:*\n${task.priority}`,
            },
            {
              type: 'mrkdwn',
              text: `*Assigned To:*\n${task.owner}`,
            },
            {
              type: 'mrkdwn',
              text: `*Due Date:*\n${task.duedate ? task.duedate : 'Not set'}`,
            },
            {
              type: 'mrkdwn',
              text: `*Status:*\n${task.status === 'Completed' ? '✅ Completed' : '❗ Incomplete'}`,
            },
          ],
        },
        {
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: `Hi *${employee.full_name}*, you have a pending task that needs attention. Please review and complete it as soon as possible.`,
          },
        },
        {
          type: 'actions',
          elements: [
            {
              type: 'button',
              text: {
                type: 'plain_text',
                text: 'View Task',
              },
              url: 'https://app.getsecureslate.com/dashboard',
            },
          ],
        },
      ]

      const FIREBASE_FUNCTION_URL = 'https://us-central1-slate-134a0.cloudfunctions.net/slackProxy'
      const response = await fetch(FIREBASE_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'postMessage',
          accessToken: slackIntegration.access_token,
          channelId,
          message: blocks,
        }),
      })

      const result = await response.json()

      if (result.success) {
        return { success: true }
      } else {
        return { success: false, error: result.error || 'Failed to send Slack message' }
      }
    } catch (error: any) {
      console.error('Error sending Slack task reminder:', error)
      return { success: false, error: error.message || 'Unknown error occurred' }
    }
  }

  /**
   * Check if any integration is available for sending reminders
   */
  isReminderAvailable(): boolean {
    return this.integrations.some((int) => int.name === 'Slack' && int.status === 'Connected')
  }

  /**
   * Get available integration names for reminders
   */
  getAvailableIntegrations(): string[] {
    return this.integrations
      .filter((int) => int.status === 'Connected')
      .map((int) => int.name)
  }

  // Future integration methods can be added here:

  /**
   * Send reminder via Microsoft Teams
   * Example implementation - uncomment and modify as needed
   */
  // private async sendTeamsReminder(employee: UserData): Promise<{ success: boolean; error?: string }> {
  //   try {
  //     const teamsIntegration = this.integrations.find(
  //       (int) => int.name === 'Teams' && int.status === 'Connected'
  //     )

  //     if (!teamsIntegration) {
  //       return { success: false, error: 'Teams integration is not connected' }
  //     }

  //     // Teams webhook URL or API implementation
  //     const webhookUrl = teamsIntegration.webhook_url
  //     if (!webhookUrl) {
  //       return { success: false, error: 'Teams webhook URL not configured' }
  //     }

  //     const message = {
  //       "@type": "MessageCard",
  //       "@context": "http://schema.org/extensions",
  //       "themeColor": "0076D7",
  //       "summary": `Onboarding Reminder for ${employee.full_name}`,
  //       "sections": [{
  //         "activityTitle": `🔔 Onboarding Reminder for ${employee.full_name}`,
  //         "activitySubtitle": "Employee Onboarding Status",
  //         "facts": [
  //           { "name": "Email", "value": employee.email },
  //           { "name": "Role", "value": employee.role },
  //           { "name": "Group", "value": employee.group },
  //           { "name": "Start Date", "value": FormatTimestamp({ timestamp: employee.start_date }) },
  //           { "name": "Status", "value": employee.onboarding === 'Completed' ? '✅ Completed' : '❗ Incomplete' }
  //         ],
  //         "markdown": true
  //       }]
  //     }

  //     const response = await fetch(webhookUrl, {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify(message)
  //     })

  //     if (response.ok) {
  //       return { success: true }
  //     } else {
  //       return { success: false, error: `Teams API error: ${response.statusText}` }
  //     }
  //   } catch (error: any) {
  //     return { success: false, error: error.message || 'Teams integration error' }
  //   }
  // }

  /**
   * Send reminder via Discord
   * Example implementation - uncomment and modify as needed
   */
  // private async sendDiscordReminder(employee: UserData): Promise<{ success: boolean; error?: string }> {
  //   try {
  //     const discordIntegration = this.integrations.find(
  //       (int) => int.name === 'Discord' && int.status === 'Connected'
  //     )

  //     if (!discordIntegration) {
  //       return { success: false, error: 'Discord integration is not connected' }
  //     }

  //     const webhookUrl = discordIntegration.webhook_url
  //     if (!webhookUrl) {
  //       return { success: false, error: 'Discord webhook URL not configured' }
  //     }

  //     const embed = {
  //       title: `🔔 Onboarding Reminder for ${employee.full_name}`,
  //       color: 0x00ff00,
  //       fields: [
  //         { name: "Email", value: employee.email, inline: true },
  //         { name: "Role", value: employee.role, inline: true },
  //         { name: "Group", value: employee.group, inline: true },
  //         { name: "Start Date", value: FormatTimestamp({ timestamp: employee.start_date }), inline: true },
  //         { name: "Status", value: employee.onboarding === 'Completed' ? '✅ Completed' : '❗ Incomplete', inline: true }
  //       ],
  //       description: `Hi **${employee.full_name}**, it looks like you haven't completed your onboarding yet. Please log in and finish the pending steps to stay on track.`,
  //       timestamp: new Date().toISOString()
  //     }

  //     const response = await fetch(webhookUrl, {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify({ embeds: [embed] })
  //     })

  //     if (response.ok) {
  //       return { success: true }
  //     } else {
  //       return { success: false, error: `Discord API error: ${response.statusText}` }
  //     }
  //   } catch (error: any) {
  //     return { success: false, error: error.message || 'Discord integration error' }
  //   }
  // }

  /**
   * Send reminder via Email
   * Example implementation - uncomment and modify as needed
   */
  // private async sendEmailReminder(employee: UserData): Promise<{ success: boolean; error?: string }> {
  //   try {
  //     const emailIntegration = this.integrations.find(
  //       (int) => int.name === 'Email' && int.status === 'Connected'
  //     )

  //     if (!emailIntegration) {
  //       return { success: false, error: 'Email integration is not connected' }
  //     }

  //     // Use your email service (SendGrid, AWS SES, etc.)
  //     const emailData = {
  //       to: employee.email,
  //       subject: `Onboarding Reminder - ${employee.full_name}`,
  //       html: `
  //         <h2>🔔 Onboarding Reminder</h2>
  //         <p>Hi <strong>${employee.full_name}</strong>,</p>
  //         <p>It looks like you haven't completed your onboarding yet. Please log in and finish the pending steps to stay on track.</p>
  //         <h3>Your Details:</h3>
  //         <ul>
  //           <li><strong>Email:</strong> ${employee.email}</li>
  //           <li><strong>Role:</strong> ${employee.role}</li>
  //           <li><strong>Group:</strong> ${employee.group}</li>
  //           <li><strong>Start Date:</strong> ${FormatTimestamp({ timestamp: employee.start_date })}</li>
  //           <li><strong>Status:</strong> ${employee.onboarding === 'Completed' ? '✅ Completed' : '❗ Incomplete'}</li>
  //         </ul>
  //       `
  //     }

  //     // Implement your email sending logic here
  //     // const result = await sendEmail(emailData)
  //     
  //     return { success: true } // or based on actual result
  //   } catch (error: any) {
  //     return { success: false, error: error.message || 'Email integration error' }
  //   }
  // }
}

/**
 * Factory function to create IntegrationNotifications instance
 */
export const createIntegrationNotifications = (integrations: Integration[], currentUser: any) => {
  return new IntegrationNotifications(integrations, currentUser)
}